import React from 'react';
import {BrowserRouter as Router} from 'react-router-dom';
import {API, Auth, Cache, Hub} from 'aws-amplify';
import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {useDispatch, useSelector} from 'react-redux';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ThemeProvider } from '@mui/material/styles';
import {EidAuthContext} from './eid/EIDAuthContext';
import {FEDERATED_PATH} from '@/common/CacheKeys';
import Gainsight from "@/components/Gainsight";
import FullStory from "@/components/FullStory";
import AppLayout from "@/AppLayout";
import {fetchUserMe} from '@/shared/user/actions';
import '@/styles/styles.sass';
import theme from '@/styles/theme';
import {CircularProgress} from "@mui/material";
import darkTheme from '@/styles/DarkTheme';
import _ from "lodash";

function App(props) {
    const [user, setUser] = React.useState(null);
    const {eidUser, eidUserSignOut} = React.useContext(EidAuthContext);
    const [authState, setAuthState] = React.useState({
        isAuthenticated: false,
        needsVerification: false,
        authenticatedUser: {},
        authLoading: true
    });
    const aboutMe = useSelector(state => state.user.aboutMe);
    const shouldLogout = useSelector(state => state.logout.shouldLogout);
    const inActiveUser = useSelector(state => state.logout.inActiveUser);
    const appliedTheme = aboutMe?.user?.lightTheme ? theme : darkTheme;
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (shouldLogout && eidUser) {
            setAuthState({
                authLoading: false,
                isAuthenticated: false,
                authenticatedUser: {},
                needsVerification: false
            })
            eidUserSignOut();
        }
    }, [shouldLogout]);

    React.useEffect(() => {
        if (shouldLogout && eidUser) {
            return;
        }
        authenticate()
        Hub.listen('auth', (data) => {
            const {payload} = data;
            if (!payload.data) {
                return;
            }
            authenticate();
        });

        Hub.listen('verify', (data) => {
            authenticate();
        });
    }, [eidUser]);

    React.useEffect(() => {
        if (shouldLogout && eidUser) {
            return;
        }
        if (inActiveUser) {
            setAuthState({
                authLoading: false,
                isAuthenticated: false,
                authenticatedUser: {},
                needsVerification: false
            })
            localStorage.setItem(`logout_message:${window.__RUNTIME_CONFIG__.API_HOST}`, "User is inactive, please contact your administrator.");
            if (eidUser && !_.isEmpty(eidUser)) {
                eidUserSignOut();
            } else {
                handleLogout();
            }
            return;
        }
        if (aboutMe != null && aboutMe.user != null) {
            setAuthState({
                authLoading: false,
                isAuthenticated: true,
                authenticatedUser: user,
                needsVerification: false
            });
            const isEidDelegationEnabled = window.__RUNTIME_CONFIG__?.EID_DELEGATION_ENABLED === 'true';
            const justSwitchedBack = sessionStorage.getItem('eid_switched_back') === 'true';
            if (justSwitchedBack) {
                sessionStorage.removeItem('eid_switched_back');
            } else if (isEidDelegationEnabled && eidUser && aboutMe?.principal?.eidSupportToolUser
                    && !window.location.pathname.startsWith('/support-tools')
                    && aboutMe?.user?.userType !== 'INTERNAL_SUPPORT') {
                window.location.replace('/support-tools');
            }
        }
    }, [aboutMe]);

    const authenticate = () => {
        if (eidUser) {
            dispatch(fetchUserMe(eidUser));
            setUser(eidUser);
            return;
        }
        Auth.currentAuthenticatedUser({bypassCache: true})
            .then((currentAuthenticatedUser) => {
                dispatch(fetchUserMe());
                setUser(currentAuthenticatedUser);
            })
            .catch((ex) => {
                setAuthState({
                    authLoading: false,
                    isAuthenticated: false,
                    authenticatedUser: {},
                    needsVerification: false
                })
            })

    }

    const handleLogout = () => {
        let path = Cache.getItem(FEDERATED_PATH);
        Cache.clear();
        Auth.signOut()
            .then(data => {
                if (path) {
                    window.location = "/" + path + "/auth"
                } else {
                    window.location = "/auth";
                }
            })
            .catch(err => console.log(err));
    }


    if (authState.authLoading || ((eidUser && !_.isEmpty(eidUser)) && !aboutMe.user)) {
        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
                <CircularProgress size="5rem"/>
            </div>
        );
    }
    const childProps = {
        isAuthenticated: authState.isAuthenticated,
        authenticatedUser: authState.authenticatedUser,
        needsVerification: authState.needsVerification
    };

    return (
        <Router>
            <Gainsight/>
            <FullStory/>
            <ToastContainer theme={appliedTheme}/>
            <ThemeProvider theme={appliedTheme}>
                <AppLayout childProps={childProps}/>
            </ThemeProvider>
        </Router>);
}


export default App;